import {get, unionBy} from "lodash";
import {getBoolean, getInteger, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";

/**
 * Unique Redux store key associated to this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'accounts';

// Define reducer types handled by this reducers
export const REDUCER_TYPES = {
	RESET: '@accounts/reset',
	SET_LIST_DATA: '@accounts/set_list_data',
	CLEAR_LIST_DATA: '@accounts/clear_list_data',
	SET_ITEM: '@accounts/set_item',
	CLEAR_ITEM: '@accounts/clear_item',
	SET_ITEM_SUB_ACCOUNTS: '@accounts/set_item_sub_accounts',
	CLEAR_ITEM_SUB_ACCOUNTS: '@accounts/clear_item_sub_accounts',
	SET_ITEM_ROLES: '@accounts/set_item_roles',
	CLEAR_ITEM_ROLES: '@accounts/clear_item_roles'
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} accountsListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	setAccountsListData: accountsListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: accountsListData
	}),
	clearAccountsListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	
	/**
	 * @param {AccountsItemDataObject} accountsItem
	 * @return {{type: string, item: AccountsItemDataObject}}
	 */
	setAccountsItem: accountsItem => ({
		type: REDUCER_TYPES.SET_ITEM, 
		item: accountsItem
	}),
	clearAccountsItem: () => ({type: REDUCER_TYPES.CLEAR_ITEM}),
	
	/**
	 * @param {AccountsItemSubAccountsDataObject[]} subAccounts
	 * @return {{type: string, subAccounts: AccountsItemSubAccountsDataObject[]}}
	 */
	setItemSubAccounts: subAccounts => ({type: REDUCER_TYPES.SET_ITEM_SUB_ACCOUNTS, subAccounts}),
	clearItemSubAccounts: () => ({type: REDUCER_TYPES.CLEAR_ITEM_SUB_ACCOUNTS}),

	/**
	 * @param {AccountsItemRoleDataObject[]} roles
	 * @return {{type: string, roles: AccountsItemRoleDataObject[]}}
	 */
	setItemRoles: roles => ({type: REDUCER_TYPES.SET_ITEM_ROLES, roles}),
	clearItemRoles: () => ({type: REDUCER_TYPES.CLEAR_ITEM_ROLES}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {AccountsItemDataObject}
	 */
	item: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {AccountsItemSubAccountsDataObject}
	 */
	itemSubAccounts: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {AccountsItemRoleDataObject[]}
	 */
	itemRoles: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};

		case REDUCER_TYPES.SET_ITEM: return {...state, item: action.item};
		case REDUCER_TYPES.CLEAR_ITEM: return {...state, item: initialState.item};
		
		case REDUCER_TYPES.SET_ITEM_SUB_ACCOUNTS: return {...state, itemSubAccounts: unionBy(action.subAccounts, 'id')};
		case REDUCER_TYPES.CLEAR_ITEM_SUB_ACCOUNTS: return {...state, itemSubAccounts: initialState.itemSubAccounts};
		
		case REDUCER_TYPES.SET_ITEM_ROLES: return {...state, itemRoles: action.roles};
		case REDUCER_TYPES.CLEAR_ITEM_ROLES: return {...state, itemRoles: initialState.itemRoles}

		default: return state;
	}
};

// Selectors
export const selectors = {
	getAccountsList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getAccountsListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getAccountsListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'listData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getAccountsListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),

	getAccountsItem: state => get(state, [reducerStoreKey, 'item']),
	getItemSubAccounts: state => get(state, [reducerStoreKey, 'itemSubAccounts']),
	getItemRoles: state => get(state, [reducerStoreKey, 'itemRoles']),
};

export default reducer;